import React from 'react';
import { Box, Typography, Card, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface BlastocystSegmentationProps {
  imageUrl?: string;
  title?: string;
  showLegend?: boolean;
  width?: string | number;
  height?: string | number;
}


const headerSx = {
  fontFamily: 'Montserrat',
  fontWeight: 600,
  fontSize: '12px',
  lineHeight: '145%',
  letterSpacing: 0,
  color: '#353535',
  textAlign: 'center',
  marginBottom: 2
};

const legendSx = {
  fontFamily: 'Montserrat',
  fontWeight: 500,
  fontSize: '12px',
  color: '#232323',
};




const BlastocystSegmentation: React.FC<BlastocystSegmentationProps> = ({
  imageUrl = '',
  title = '',
  showLegend = true,
  width = '100%',
  height = 'auto'
}) => {
  
  const { t: tAiGrading } = useTranslation('aiGrading_config');


  const legendItems = [
    { color: '#D6D6D6', label: 'background' },
    { color: '#A541DF', label: 'innerCellMass' },
    { color: '#399FC5', label: 'blastocoel' },
    { color: '#35A11A', label: 'trophectoderm' },
    { color: '#F09A59', label: 'zonaPellucida' }
  ];



  const cardSx = {
    padding: 2,
    borderRadius: 4,
    width: width,
    height: height,
    backgroundColor: '#FFFFFF',
    border: '1px solid #F0F0F0',
    display: 'flex',
    flexDirection: 'column' as const,

  };

  const segmentationImageBoxSx = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F5F5F5',
    borderRadius: 4,
    padding: 2,
    marginBottom: showLegend ? 2 : 0,
    minHeight: 200,
    border: '1px solid #E0E0E0',
    flex: 1
  };


  return (
    <Card
      elevation={0}
      sx={cardSx}
    >
      {/* Title */}
      <Typography sx={headerSx}>
        {title}
      </Typography>

      {/* Segmentation Image */}
      <Box
        sx={segmentationImageBoxSx}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt="Blastocyst Segmentation"
            style={{
              maxWidth: '300px',
              objectFit: 'contain',
              borderRadius: '8px',
              width: '100%'
            }}
          />
        ) : (
          // Placeholder when no image is provided
          <Box
            sx={{
              width: 180,
              height: 180,
              borderRadius: '50%',
              background: 'linear-gradient(45deg, #87CEEB 25%, #98FB98 25%, #98FB98 50%, #DDA0DD 50%, #DDA0DD 75%, #F4A460 75%)',
              border: '3px solid #E0E0E0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}
          >
            {/* Placeholder segmentation visualization */}
            <Box
              sx={{
                width: '60%',
                height: '60%',
                backgroundColor: '#87CEEB',
                borderRadius: '50%',
                position: 'relative'
              }}
            >
              <Box
                sx={{
                  width: '40%',
                  height: '40%',
                  backgroundColor: '#DDA0DD',
                  borderRadius: '50%',
                  position: 'absolute',
                  bottom: '10%',
                  right: '10%'
                }}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Legend */}
      {showLegend && (
        <Stack direction="row" spacing={2} flexWrap="wrap" justifyContent="center">
          {legendItems.map((item, index) => (
            <Box key={index} display="flex" alignItems="center" gap={0.5} >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: item.color,
                  borderRadius: '50%',
                  border: '1px solid #ccc',
                  flexShrink: 0
                }}
              />
              <Typography sx={legendSx}>
                { tAiGrading(item.label) }
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Card>
  );
};

export default BlastocystSegmentation;